import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { trackEvent } from '@v2/shared/lib/analytics';
import useRecaptcha from '@v2/shared/lib/useRecaptcha';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import {
  ERROR_MESSAGES,
  FIELD_MESSAGES,
  ORDER_REASON_IDS,
  REASON_GROUPS,
  SERVER_FIELD_ERRORS,
} from './constants';
import type { ContactFormData, ContactPageProps, SubmitError, SubmitState } from './types';

/**
 * Where the form posts, scripted or not.
 *
 * `formType=simple` is the branch that takes `Subject` as a subject id straight off
 * TicketSubjectListBuilder's list, rather than as an index into the nested
 * order-question tree the page this replaced walked.
 */
export const ACTION = '/contact.php?xCmd=send';
export const FORM_TYPE = 'simple';

/** The dropdown's own word for a reason, for the analytics event. */
function reasonLabel(subject: string): string {
  const id = Number(subject);

  for (const group of REASON_GROUPS) {
    const match = group.reasons.find((reason) => reason.id === id);

    if (match) {
      return match.label;
    }
  }

  return '';
}

export default function useContactForm({
  customer,
  sent,
  initialSubject,
}: Pick<ContactPageProps, 'customer' | 'sent' | 'initialSubject'>) {
  const [submitState, setSubmitState] = useState<SubmitState>(sent ? 'success' : 'idle');
  const [submitError, setSubmitError] = useState<SubmitError>(null);
  /**
   * Whether the "can't find your order" prompt has already asked once.
   *
   * The prompt interrupts the first submit and never the second, so a visitor who has
   * nothing to add is delayed by one click and not blocked -- these three fields are
   * optional, and a message that cannot be sent is worse than one that takes a reply to
   * place.
   */
  const [hasAskedForOrder, setHasAskedForOrder] = useState(false);
  const { executeRecaptcha } = useRecaptcha();
  const { buildUrl } = useUrlGenerator();
  const findOrderRef = useRef<HTMLFieldSetElement | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    setFocus,
    control,
    formState: { errors },
  } = useForm<ContactFormData>({
    mode: 'onTouched',
    defaultValues: {
      name: customer?.name ?? '',
      EmailAddress: customer?.email ?? '',
      order: '',
      Subject: initialSubject ? String(initialSubject) : '',
      magazines: '',
      recipientName: '',
      recipientZip: '',
      cfComments: '',
    },
  });

  /**
   * Selects a reason from one of the topic cards and moves to the form.
   *
   * The scroll is what makes a card's "message us about this" link mean anything: the
   * form is several screens down, and a link that silently changed a dropdown nobody can
   * see reads as a dead link.
   */
  const pickReason = useCallback(
    (reason: number) => {
      setValue('Subject', String(reason), { shouldValidate: true });
      document.getElementById('message')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [setValue]
  );

  /**
   * Puts a refusal back where it belongs: each field contact.php flagged gets its own
   * message, and the banner names the reason. Returns the banner's kind.
   */
  const applyRefusal = (result: Record<string, unknown>): NonNullable<SubmitError> => {
    // The field errors are applied before the banner is chosen, not instead of it.
    // contact.php validates the fields and checks the captcha independently and can flag
    // both in one reply -- a name of spaces passes the browser's `required` and fails the
    // server's `chop()` before NOTBLANK -- and returning early on the captcha used to
    // throw those flags away. The visitor fixed the captcha, submitted again, and was
    // refused again by a field that had never been marked.
    Object.entries(SERVER_FIELD_ERRORS).forEach(([flag, field]) => {
      if (result[flag] === 'Y') {
        setError(field, { type: 'server', message: FIELD_MESSAGES[field] });
      }
    });

    if (result.captcha_error === 'Y') {
      return 'captcha';
    }

    if (result.ticket_error === 'Y') {
      return 'ticket';
    }

    return 'validation';
  };

  const onSubmit = async (data: ContactFormData) => {
    // One interruption, before anything is sent: an order question with nothing to find
    // the order by costs a round trip that a name and a ZIP would have saved.
    if (
      !hasAskedForOrder &&
      ORDER_REASON_IDS.includes(Number(data.Subject)) &&
      !data.order.trim() &&
      !data.recipientName.trim() &&
      !data.recipientZip.trim()
    ) {
      setHasAskedForOrder(true);
      findOrderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFocus('recipientName');

      return;
    }

    setSubmitState('submitting');
    setSubmitError(null);

    try {
      const params = new URLSearchParams({
        ...data,
        formType: FORM_TYPE,
        'g-recaptcha-response': await executeRecaptcha(),
      });

      const response = await fetch(buildUrl(`${ACTION}&ajax=1`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const result = await response.json();

      if (result.error === 'N') {
        trackEvent('contact_form_submit', {
          subject: Number(data.Subject),
          reason: reasonLabel(data.Subject),
        });
        setSubmitState('success');

        return;
      }

      setSubmitError(applyRefusal(result));
      setSubmitState('error');
    } catch (error) {
      // Surfaced rather than swallowed: a message that never arrives is a customer who
      // thinks they have been ignored, and they are told to try again either way.
      console.error('Contact form submission failed', error);
      setSubmitError('ticket');
      setSubmitState('error');
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    action: buildUrl(ACTION),
    submitState,
    submitMessage: submitError ? ERROR_MESSAGES[submitError] : null,
    isSubmitting: submitState === 'submitting',
    /**
     * The form's control, for the fields the form itself watches.
     *
     * This hook is called by the page, not by the form -- the topic cards select a
     * reason and they sit several screens above it -- so anything watched *here* would
     * re-render the whole page on every keystroke: the hero, the glance strip, four
     * topic cards and six accordions, for a character typed into an optional field.
     * Handing the control down lets the form subscribe to its own fields with useWatch
     * and keeps those re-renders inside the form.
     */
    control,
    /** Whether the "can't find your order" prompt has already interrupted one submit. */
    hasAskedForOrder,
    /**
     * The reason `?xSubject=` asked for, for the `<select>`'s own defaultValue.
     *
     * react-hook-form applies its defaultValues through the ref after mount, which the
     * server has not done, so without this the option is marked selected only once the
     * bundle has hydrated -- and never at all for a visitor with scripting off, who
     * would be sent from a "contact us about this" link to a dropdown that had forgotten
     * what it was about. Same value as the form's own default, so the two agree and
     * hydration has nothing to correct.
     */
    defaultSubject: initialSubject ? String(initialSubject) : '',
    pickReason,
    findOrderRef,
  };
}
